// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Component Imports
import { Box, Typography, Tooltip } from "@mui/material";

// ** Custom Component Imports
import CustomChip from "../../../components/mui/chip";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Logic Imports
import { GetInvoice } from "./GetInvoice";
import { convertESTToLocal } from "components/formatDate";
import { formatUSCurrency } from "components/CurrencyFormat";
import { useInvoicePermission } from "../permissions/InvoicePermissions";

export const useIndexLogic = () => {
  //#region Permissions
  const { InvoicePermissionProps } = useInvoicePermission();
  const {
    hasInvoiceSavePermission,
    hasInvoiceGetPermission,
    hasInvoiceDeletePermission,
    hasInvoiceSendEstEmailPermission,
    hasInvoiceSendEmailPermission,
  } = InvoicePermissionProps;
  //#endregion Permissions

  //#region Hooks
  const navigate = useNavigate();
  //#endregion

  //#region Constants
  const contentHeightFixed = false;
  //#endregion End of Constants

  //#region States
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationText, setPaginationText] = useState('');
  const [openNewWorkOrderDlg, setOpenNewWorkOrderDlg] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openTagManagementDlg, setOpenTagManagementDlg] = useState(false);
  const [schedulingReq, setSchedulingReq] = useState(false);
  const [openScheduleAst, setOpenScheduleAst] = useState(false);

  const tagManagementData = { title: "Tag Management" };
  
  const customerID = 0;
  const requestID = 0;
  const columns = [
    {
      flex: 0.1,
      field: "id",
      hide: true,
      minWidth: 130,
      headerName: "Invoice ID",
      renderCell: ({ row }) => {
        const { invoiceID, id } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/invoices/detail/${invoiceID}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{id}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "customerName",
      minWidth: 170,
      headerName: "Customer",
      renderCell: ({ row }) => {
        const { customerName, customerID } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" ,
             cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
          }, }} 
          onClick={() => navigate(`/customers/detail/${customerID}`)}>
            <Typography sx={{ flexGrow: 1 }}>{customerName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "createDate",
      minWidth: 140,
      headerName: "Created On",
      renderCell: ({ row }) => {
        const { createDate } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{convertESTToLocal(createDate)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "createdBy",
      headerName: "Created By",
      minWidth: 140,
      renderCell: ({ row }) => {
        const { createdBy } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{createdBy.trim() ? createdBy : 'System Generated'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "dueDate",
      headerName: "Invoice Due Date",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { dueDate } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{convertESTToLocal(dueDate)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "subTotal",
      headerName: "Invoice Total ($)",
      minWidth: 160,
      renderCell: ({ row }) => {
        const { subTotal } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1, color: subTotal < 0? 'error.main' : 'inherit' }}>{formatUSCurrency(subTotal)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "paymentReceived",
      headerName: "Received ($)",
      minWidth: 140,
      renderCell: ({ row }) => {
        const { paymentReceived } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 ,color: paymentReceived < 0? 'error.main' : 'inherit' }}>{formatUSCurrency(paymentReceived)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "paymentRemaining",
      headerName: "Remaining ($)",
      minWidth: 140,
      renderCell: ({ row }) => {
        const { paymentRemaining } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 ,color: paymentRemaining < 0? 'error.main' : 'inherit' }}>{formatUSCurrency(paymentRemaining)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { status } = row;

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={
              status === "Paid" ? "success" : 
              status === "Partially Paid" ? "warning" :
              status === "Sent" ? "info" :
              status === "Cancelled" ? "error" :
              "secondary"
            }
            label={status}
          />
        );
      },
    },
    {
      flex: 0.1,
      field: "modifiedDate",
      headerName: "Last Updated",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { modifiedDate } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{modifiedDate ? convertESTToLocal(modifiedDate) : ''}</Typography>
          </Box>
        );
      },
    },
  ];

  //#endregion end of states

  //#region Handlers and methods

  useEffect(() => {
    if(hasInvoiceGetPermission){
      fetchInvoices();
    }else{
      setLoading(false);
    }
  }, [hasInvoiceGetPermission])
  
  useEffect(() => {
    if(hasInvoiceGetPermission){
      setFilteredRows(() =>
        data.filter((row) =>
          Object.values(row).some(
            (value) =>
              value &&
              value.toString().toLowerCase().includes(searchText.toLowerCase())
          )
        )
      );
    }else{
      return;
    }
  }, [searchText]);

  useEffect(() => {
    const total = filteredRows.length;
    let start = paginationModel.page * paginationModel.pageSize + 1;
    let end = Math.min((paginationModel.page + 1) * paginationModel.pageSize, total);
  
    if (total === 0) {
      setPaginationText("0-0 of 0");
      return;
    }

    if (paginationModel.pageSize === -1) {
      start = 1;
      end = total;
    }
  
    setPaginationText(`${start}-${end} of ${total}`);
  }, [filteredRows, paginationModel.page, paginationModel.pageSize]);

  

    const fetchInvoices = async () => {
      if(!hasInvoiceGetPermission) return;

      try {
        setLoading(true);
        let invoices = await GetInvoice(0,0, 50,1, navigate); 
        const modifiedInvoices =  invoices.map(({ invoiceID, ...rest }) => ({
          id: "INV-"+invoiceID.toString().padStart(3, "0"),
          invoiceID: invoiceID,        
          ...rest  
        }));               
        setData(modifiedInvoices);
        setFilteredRows(modifiedInvoices);
      } catch (error) {
        console.error("Error fetching Requests data:", error);
      } finally {
        setLoading(false);
      }
    };

  const handleOpenScheduleAstDlg = () => {
    setOpenScheduleAst(true);
  };

  const handleCloseScheduleAstDlg = () => {
    setOpenScheduleAst(false);
  };

  const handleOpenNewWorkOrderDlg = () => {
    setOpenNewWorkOrderDlg(true);
  };
  const handleCloseNewWorkOrderDlg = () => {
    setOpenNewWorkOrderDlg(false);
  };

  const handleOpenTagManagementDlg = () => setOpenTagManagementDlg(true);
  const handleCloseTagManagementDlg = () => setOpenTagManagementDlg(false);

  const handleClearSelection = () => {
    setRowSelectionModel([]);
    setSelectedRows(0);
  };

  const handleGridSelection = (selectedRows) => setSelectedRows(selectedRows);

  const handleSearch = (val) => setSearchText(val);

  const handleClearSearch = () => setSearchText("");

  //#endregion end of handlers and methods

  const IndexProps = {
    columns,
    contentHeightFixed,
    handleOpenNewWorkOrderDlg,
    handleCloseNewWorkOrderDlg,
    schedulingReq,
    setSchedulingReq,
    openScheduleAst,
    setOpenScheduleAst,
    handleOpenScheduleAstDlg,
    handleCloseScheduleAstDlg,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    openNewWorkOrderDlg,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    openTagManagementDlg,
    tagManagementData,
  };

  return {
    IndexProps,
  };
};
